/**
 * AdminLoading - Единые паттерны загрузки для админки
 * Заменяет все дублированные skeleton/spinner реализации
 * 
 * CANON compliance: Consistent UI, Human-Readable loading states
 */
import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { InlineLoadingIndicator } from '@/components/ui/inline-loading';
import { useTranslation } from 'react-i18next';

export interface AdminLoadingProps {
  variant: 'skeleton' | 'spinner' | 'table' | 'card' | 'inline';
  /** For table/card variants */
  rows?: number;
  columns?: number;
  /** Custom message */
  message?: string;
  /** Size for spinner */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function AdminLoading({ 
  variant, 
  rows = 5, 
  columns = 4, 
  message,
  size = 'md',
  className = '' 
}: AdminLoadingProps) {
  const { t } = useTranslation('common');

  switch (variant) {
    case 'spinner':
      return (
        <div className={`flex items-center gap-2 justify-center p-8 ${className}`}>
          <InlineLoadingIndicator
            size={size}
            aria-label={message || t('loading', 'Loading...')}
          />
          {message && <span className="text-muted-foreground">{message}</span>}
        </div>
      );

    case 'table':
      return (
        <div className={`space-y-4 ${className}`}>
          {/* Table header skeleton */}
          <div className="flex gap-4">
            {[...Array(columns)].map((_, i) => (
              <Skeleton key={`header-${i}`} className="h-4 flex-1" />
            ))}
          </div>
          {/* Table rows skeleton */}
          {[...Array(rows)].map((_, rowIndex) => (
            <div key={`row-${rowIndex}`} className="flex gap-4">
              {[...Array(columns)].map((_, colIndex) => (
                <Skeleton key={`cell-${rowIndex}-${colIndex}`} className="h-8 flex-1" />
              ))}
            </div>
          ))}
        </div>
      );

    case 'card':
      return (
        <div className={`space-y-4 ${className}`}>
          {[...Array(rows)].map((_, i) => (
            <div key={`card-${i}`} className="border rounded-lg p-4 space-y-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          ))}
        </div>
      );

    case 'inline':
      return (
        <div className={`flex items-center gap-2 ${className}`}>
          <InlineLoadingIndicator
            size={size}
            aria-label={message || t('loading', 'Loading...')}
          />
          <span
            className="text-xs text-muted-foreground"
            role="status"
            aria-live="polite"
          >
            {message || t('loading', 'Loading...')}
          </span>
        </div>
      );

    case 'skeleton':
    default:
      return (
        <div className={`space-y-2 ${className}`}>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      );
  }
}

// Predefined common loading states
export const AdminTableSkeleton = ({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) => (
  <AdminLoading variant="table" rows={rows} columns={columns} />
);

export const AdminCardSkeleton = ({ count = 3 }: { count?: number }) => (
  <AdminLoading variant="card" rows={count} />
);

export const AdminSpinner = ({ message, size = 'md' }: { message?: string; size?: 'sm' | 'md' | 'lg' }) => (
  <AdminLoading variant="spinner" message={message} size={size} />
);

interface AdminInlineLoadingProps {
  message?: string;
  role?: 'status' | 'alert' | 'log';
  'aria-live'?: 'polite' | 'assertive' | 'off';
}

export const AdminInlineLoading = ({ message, role: _role, 'aria-live': _ariaLive }: AdminInlineLoadingProps) => (
  // a11y attributes are built into AdminLoading inline variant
  <AdminLoading variant="inline" message={message} />
);